/**
 * ConnectivityBanner — single-line visual indicator for the provider portal.
 *
 * Three states:
 *   online        -> hidden (no banner)
 *   reconnecting  -> yellow, "Reconectando…"
 *   offline       -> red, "Sin conexión · mostrando datos en caché. Acciones deshabilitadas."
 *
 * Banner is purely informational. Actions across the portal check
 * isOffline themselves to disable mutating UI.
 */
import { Wifi, WifiOff, Loader2 } from 'lucide-react';
import { useConnectivity } from '@/hooks/useConnectivity';

export const ConnectivityBanner = () => {
  const { mode } = useConnectivity();
  if (mode === 'online') return null;

  if (mode === 'reconnecting') {
    return (
      <div className="bg-amber-100 border-b border-amber-300 px-4 py-1.5 text-xs flex items-center gap-2 text-amber-900" data-testid="connectivity-banner-reconnecting">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Reconectando al canal en tiempo real. La información puede tardar unos segundos en actualizarse.</span>
      </div>
    );
  }

  return (
    <div className="bg-rose-100 border-b border-rose-300 px-4 py-1.5 text-xs flex items-center gap-2 text-rose-900" data-testid="connectivity-banner-offline">
      <WifiOff className="w-3.5 h-3.5" />
      <span>Sin conexión · mostrando datos en caché. Las acciones (transitions / uploads / verificaciones) están deshabilitadas hasta restaurar la red.</span>
    </div>
  );
};

export const OnlinePill = () => {
  const { mode } = useConnectivity();
  const cfg = {
    online:       { Icon: Wifi,    cls: 'text-emerald-600 bg-emerald-50 ring-emerald-300', label: 'en línea' },
    reconnecting: { Icon: Loader2, cls: 'text-amber-700 bg-amber-50 ring-amber-300',       label: 'reconectando' },
    offline:      { Icon: WifiOff, cls: 'text-rose-700 bg-rose-50 ring-rose-300',          label: 'sin conexión · caché' },
  }[mode];
  const { Icon, cls, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ring-inset ${cls}`} data-testid="connectivity-pill">
      <Icon className={`w-3 h-3 ${mode === 'reconnecting' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
};

export default ConnectivityBanner;
